/**
 * WHAT IS STILL MISSING BEFORE THE PIECE CAN GO TO AN EDITOR - the line under the editor's top row
 * (design 15, the owner's live look at real cabinets, 2026-10-01).
 *
 * The prototype draws an amber line, "Add a cover image, 3 tags, 600 words before submitting." Its
 * three items and its 600 are its own and not this platform's (./SubmitChecklist.tsx's header says
 * why the 600 is unrepresentable here), so this line is BUILT FROM THE CHECKLIST: the rows whose
 * badge reads "Not yet", in the checklist's own words and order - nothing is named here that the
 * checklist does not name, and a row the checklist cannot check ("Cannot check") is not counted as
 * missing, exactly as the count beside it never counted one. With nothing missing it is not drawn.
 *
 * ★ THE COUNT IS THE BADGE IT ALWAYS WAS, moved into this line from the row of badges the old header
 * carried - "1 thing to fix before submitting", "3 things to fix before submitting" (`thingsToFix`). It
 * read "N thing(s)" until the owner's look at the editor (2026-10-05): an author reads one or many,
 * never a bracket. web/authors/test/tinted-inks.spec.tsx measures it by those words. Its words take
 * `--mx-text` now: the line lays its own gold tint under the badge's, and the text ink reads on any
 * tint in both schemes (./editor-inks.spec.tsx measures the stack).
 *
 * "See the checklist" brings the side panel's Checklist tab forward, so the detail behind each name
 * is one press away.
 */
import { Badge } from '@mantine/core';
import { Info } from '@moonx/ui';
import { useTranslation } from '../../../i18n';
import type { ArticlesT } from '../articles-i18n';
import type { ChecklistRow } from './document';
import classes from './editor.module.css';

export interface MissingBeforeSendingProps {
  /** Every checklist row, as the Checklist tab draws them. */
  readonly rows: readonly ChecklistRow[];
  /** Brings the Checklist tab forward. */
  readonly onOpenChecklist: () => void;
}

/** The rows the line names: the ones the checklist says are not ready yet, in its order. */
export function missingRows(rows: readonly ChecklistRow[]): readonly ChecklistRow[] {
  return rows.filter((row) => row.state === 'failing');
}

/** The count's words: one thing, or many things - never "thing(s)". */
export function thingsToFix(count: number, t: ArticlesT): string {
  return t('editor.missing.thingsToFix', { count });
}

/** The line's own ground, inline so a spec reads what the words sit on rather than assuming it. */
export const MISSING_LINE_TINT = 'oklch(var(--mx-accent) / 0.08)';

export function MissingBeforeSending({ rows, onOpenChecklist }: MissingBeforeSendingProps) {
  const { t } = useTranslation('articles');
  const missing = missingRows(rows);
  if (missing.length === 0) {
    return null;
  }
  return (
    <div
      className={classes.banner}
      role="group"
      aria-label={t('editor.missing.label')}
      style={{
        backgroundColor: MISSING_LINE_TINT,
        border: '1px solid oklch(var(--mx-accent) / 0.45)',
      }}
    >
      <Info size={15} className={classes.bannerIcon} aria-hidden />
      <Badge
        component="span"
        size="sm"
        radius="sm"
        styles={{
          root: {
            backgroundColor: 'oklch(var(--mx-warning) / 0.14)',
            color: 'oklch(var(--mx-text))',
          },
        }}
      >
        {thingsToFix(missing.length, t)}
      </Badge>
      <ul className={classes.bannerList}>
        {missing.map((row, index) => (
          <li key={row.rule}>
            {index === 0 ? null : (
              <span className={classes.bannerSeparator} aria-hidden="true">
                ·
              </span>
            )}
            {row.label}
          </li>
        ))}
      </ul>
      <button type="button" className={classes.bannerLink} onClick={onOpenChecklist}>
        {t('editor.missing.seeChecklist')}
      </button>
    </div>
  );
}
